import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { UserService } from 'src/app/services/user.service';
import { ToastController, AlertController, PickerController, Platform, IonInput } from '@ionic/angular';
import { Options } from './manage-network-options'
import moment from 'moment';
import { Router } from '@angular/router';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { KilnService } from 'src/app/services/kiln.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-network',
  templateUrl: './manage-network.component.html',
  styleUrls: ['./manage-network.component.scss'],
})
export class ManageNetworkComponent implements OnInit {
  @Input() onDismiss;
  @Input() startAddKiln = false;
  public selected_tab = 'kilns';

  public kilns = [];
  public selectedKiln = null;

  public network_devices = [];
  public selectedNetworkDevice = null;

  public manageKilnForm: UntypedFormGroup;
  public manageNetworkDeviceForm: UntypedFormGroup;

  public updating;
  public loginData;
  public hasElevatedPermissions;
  public new_kiln = false;

  /** Add-flow: 'scan' | 'serial' | 'details' when adding a new kiln with optional barcode scan */
  public addModeStep: 'scan' | 'serial' | 'details' | null = null;
  public addFlowSerial = '';
  public addFlowError = '';
  public submittingNewKiln = false;

  private accountInformation;
  public canAdminister;
  public retrievingInformation;

  public model_options = Options.all;
  public phase_options = ["Single Phase", "Two Phase", "Three Phase", "Other"];
  public voltage_options = ["115V", "208V", "240V", "Other"];
  @Input() small_screen = false;

  @ViewChild('kilnNameInput', { static: false }) kilnNameInput?: IonInput;

  constructor(public accountService: AccountService,
    public userService: UserService,
    public toastCtrl: ToastController,
    private alertController: AlertController,
    private pickerController: PickerController,
    private platform: Platform,
    private router: Router,
    private barcodeScanner: BarcodeScanner,
    private kilnService: KilnService,
    private translate: TranslateService
  ) {
      let login_data = this.userService.getLoginData();
      this.loginData = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? login_data.loggedInAs : login_data;
      this.retrievingInformation = true;

      this.hasElevatedPermissions = this.userService.hasElevatedPermissions();
      this.accountService.getAccountInformation().then((response) => {
        this.accountInformation = response;
        this.setUpCanAdminister(response);

        let current_user_permissions_for_account = response.users ? response.users.find((u) => { return u.email === this.loginData.email }) : {type: 'basic'}
        this.hasElevatedPermissions = current_user_permissions_for_account && current_user_permissions_for_account.type && current_user_permissions_for_account.type === 'admin';
      })
      this.setSmallScreen();
  }

  ngOnInit() {
    this.getAccountDevices();
    this.hasElevatedPermissions = this.userService.hasElevatedPermissions();
    this.updating = false;
    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';

    this.setSmallScreen();
    if (this.startAddKiln) {
      this.startAddFlow();
    }
  }

  setSmallScreen() {
    let is_device = this.platform ? (this.platform.is('mobile') || this.platform.is('tablet') || this.platform.is('phablet') || this.platform.is('mobileweb')) : false;
    this.small_screen = this.platform.width() < 875 || is_device;
  }

  private getAccountDevices() {
    this.accountService.getDevicesForAccount()
        .then((data) => {
          this.kilns = (data.kilns || []).sort((a, b) => (a.name > b.name) ? 1 : -1);
          this.network_devices = (data.network_devices || []).filter((nd) => nd.id !== '-1').sort((a, b) => (a.name > b.name) ? 1 : -1);
          if (this.kilns.length > 0 && !this.new_kiln) {
            this.selectKiln(this.kilns[0]);
          } else if (this.kilns.length === 0 && !this.new_kiln) {
            this.selectedKiln = null;
          }
          if (this.network_devices.length > 0) {
            this.selecteNetworkDevice(this.network_devices[0]);
          }
          this.retrievingInformation = false;
        }).catch( (error) => { this.retrievingInformation = false; console.log(error); })
  }

  selectKiln(kiln) {
    this.selectedKiln = kiln;
    let name = new UntypedFormControl(kiln ? kiln.name : 'Please enter a kiln name', [Validators.required]);
    let model = new UntypedFormControl(kiln ? kiln.model : 'Model', []);
    let voltage = new UntypedFormControl(kiln ? kiln.voltage : 'Voltage', []);
    let phase = new UntypedFormControl(kiln ? kiln.phase : 'Phase', []);
    let serial_number = new UntypedFormControl(kiln ? kiln.serial_number : 'Serial No.', []);
    let kiln_id = new UntypedFormControl(kiln ? kiln.kiln_id : 'Kiln ID', [Validators.min(1)]);
    let install_date = new UntypedFormControl((kiln && kiln.install_date) ? moment(kiln.install_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"), []);
    let nd = new UntypedFormControl(kiln && (kiln.network_device_id), []);
    let kiln_serial_number = new UntypedFormControl(kiln && (kiln.kiln_serial_number), []);

    this.manageKilnForm = new UntypedFormGroup({
      name: name,
      model: model,
      voltage: voltage,
      phase: phase,
      serial_number: serial_number,
      kiln_id: kiln_id,
      install_date: install_date,
      network_device: nd,
      is_kmt: new UntypedFormControl(kiln ? (kiln.is_kmt || false) : false, [Validators.required]),
      mac_address: new UntypedFormControl(kiln ? kiln.mac_address : "", []),
      external_id: new UntypedFormControl(kiln ? kiln.id : null),
      kiln_serial_number: kiln_serial_number
    });
  }

  selecteNetworkDevice(network_device) {
    this.new_kiln = false;
    this.selectedNetworkDevice = network_device;
    let name = new UntypedFormControl(network_device ? network_device.name : 'Please enter a device name', [Validators.required]);
    let mac_address = new UntypedFormControl(network_device ? network_device.mac_address : 'Mac Address', [Validators.required]);
    let model = new UntypedFormControl(network_device ? network_device.model : 'Model', [Validators.required]);
    let serial_number = new UntypedFormControl(network_device ? network_device.serial_number : 'Serial Number', [Validators.required]);
    let device_manufacturer = new UntypedFormControl(network_device ? network_device.device_manufacturer : 'Device Manufacturer', [Validators.required]);

    this.manageNetworkDeviceForm = new UntypedFormGroup({
      name: name,
      mac_address: mac_address,
      model: model,
      serial_number: serial_number,
      device_manufacturer: device_manufacturer
    });
  }

  submitKiln(formValue) {
    this.updating = true;
    let serial = this.new_kiln ? undefined : formValue.external_id;
    this.accountService.upsertKilnForAccount(serial, {...{type: "kiln"}, ...formValue})
        .then((response) => {
          this.userService.setCurrentAccount(this.accountInformation.general.external_id);
          this.upsertSuccess(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.KILN_WAS_CREATED' : 'MANAGE_NETWORK.KILN_WAS_UPDATED'));
        })
        .catch((error) => {
          this.upsertFailed(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.COULD_NOT_CREATE_KILN' : 'MANAGE_NETWORK.COULD_NOT_UPDATE_KILN'));
        })
  }

  submitNetworkDevice(formValue) {
    this.updating = true;
    this.accountService.upsertNetworkDeviceForAccount(formValue.serial_number, {...{type: "network_device"}, ...formValue})
        .then((response) => {
          this.userService.setCurrentAccount(this.accountInformation.general.external_id);
          this.upsertSuccess(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.NETWORK_DEVICE_WAS_CREATED' : 'MANAGE_NETWORK.NETWORK_DEVICE_WAS_UPDATED'));
        })
        .catch((error) => {
          this.upsertFailed(this.translate.instant(this.new_kiln ? 'MANAGE_NETWORK.COULD_NOT_CREATE_NETWORK_DEVICE' : 'MANAGE_NETWORK.COULD_NOT_UPDATE_NETWORK_DEVICE'));
        })
  }

  submit() {
    if (this.new_kiln && (this.addModeStep === 'details' || this.addModeStep === null)) {
      this.submitNewKiln();
    } else {
      this.submitKiln(this.manageKilnForm.value);
    }
  }

  async upsertSuccess(message) {
    this.new_kiln = false;
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.getAccountDevices();
    this.updating = false;
    this.submittingNewKiln = false;
    this.showMessage(message || this.translate.instant('MANAGE_NETWORK.UPDATE_SUCCESSFUL'));
  }

  async upsertFailed(error) {
    this.updating = false;
    const message = this.getUserFriendlyErrorMessage(error, this.translate.instant('MANAGE_NETWORK.UPDATE_FAILED'));
    this.showMessage(message);
  }

  /** Returns a user-friendly message; hides raw HTTP/500 errors */
  private getUserFriendlyErrorMessage(err: any, fallback?: string): string {
    const msg = err?.error?.message || err?.message || (typeof err === 'string' ? err : '');
    const isTechnicalError = typeof msg === 'string' && (
      msg.includes('Http failure') ||
      msg.includes('500') ||
      msg.includes('Internal Server Error') ||
      msg.includes('InternalServerError')
    );
    if (isTechnicalError) {
      return this.translate.instant('MANAGE_NETWORK.SORRY_SOMETHING_WENT_WRONG');
    }
    return msg || fallback || '';
  }

  async showMessage(message) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: 'middle',
      cssClass: 'success'
    });

    await toast.present();
  }

  addKiln() {
    this.startAddFlow();
  }

  /** Start add-kiln flow: show scan step, then serial or details */
  startAddFlow() {
    this.addFlowError = '';
    this.addFlowSerial = '';
    this.new_kiln = true;
    this.addModeStep = 'scan';
    this.selected_tab = 'kilns';
    this.buildFormForNewKiln('');
  }

  goToSerialStep() {
    this.addModeStep = 'serial';
    this.addFlowError = '';
  }

  /** Move from scan/serial to details form; optional serial from scan or manual */
  goToDetailsStepFromAdd() {
    this.addFlowError = '';
    this.addModeStep = 'details';
    this.buildFormForNewKiln(this.addFlowSerial || '');
    this.focusKilnName();
  }

  /** Skip scan/serial and go straight to details form */
  skipToDetailsFromAdd() {
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.addModeStep = 'details';
    this.buildFormForNewKiln('');
    this.focusKilnName();
  }

  cancelAddFlow() {
    this.addModeStep = null;
    this.addFlowSerial = '';
    this.addFlowError = '';
    this.new_kiln = false;
    if (this.kilns && this.kilns.length > 0) {
      this.selectKiln(this.kilns[0]);
    } else {
      this.selectedKiln = null;
    }
  }

  private buildFormForNewKiln(serial: string) {
    this.manageKilnForm = new UntypedFormGroup({
      name: new UntypedFormControl("", [Validators.required]),
      model: new UntypedFormControl("", [Validators.required]),
      voltage: new UntypedFormControl("", []),
      phase: new UntypedFormControl("", []),
      serial_number: new UntypedFormControl(serial, []),
      kiln_id: new UntypedFormControl("", [Validators.min(1)]),
      install_date: new UntypedFormControl(moment().format("YYYY-MM-DD"), []),
      network_device: new UntypedFormControl("", []),
      is_kmt: new UntypedFormControl(true, [Validators.required]),
      mac_address: new UntypedFormControl("", []),
      kiln_serial_number: new UntypedFormControl("", []),
      external_id: new UntypedFormControl(null, [])
    });
  }

  private focusKilnName() {
    if (!this.kilnNameInput) {
      return;
    }
    setTimeout(() => {
      this.kilnNameInput?.setFocus();
      this.kilnNameInput?.getInputElement().then((el) => {
        el.select();
      }).catch(() => {});
    }, 200);
  }

  scanCode() {
    this.addFlowError = '';
    this.barcodeScanner.scan({
      preferFrontCamera: false,
      showFlipCameraButton: false,
      showTorchButton: true,
      torchOn: false,
      prompt: 'Center the barcode inside the scan area',
      resultDisplayDuration: 500,
      formats: 'QR_CODE,CODE_39,UPC_A,UPC_B',
      disableAnimations: true,
      disableSuccessBeep: false
    }).then((barcodeData) => {
      this.checkScanData(barcodeData);
    }, (err) => {
      this.addFlowError = this.getUserFriendlyErrorMessage(err, this.translate.instant('MANAGE_NETWORK.SCAN_FAILED_TRY_MANUAL'));
    });
  }

  checkScanData(data: { cancelled?: boolean; text?: string }) {
    if (data && data.cancelled === false && data.text) {
      this.addFlowSerial = (data.text || '').trim();
      if (!this.addFlowSerial) {
        this.addFlowError = this.translate.instant('MANAGE_NETWORK.SERIAL_EMPTY_TRY_AGAIN');
      } else {
        this.goToDetailsStepFromAdd();
      }
    }
  }

  /** True when name (≥4 chars) and controller serial (if touchscreen) are present so Create Kiln can be enabled */
  canSubmitNewKiln(): boolean {
    if (!this.manageKilnForm) return false;
    const name = (this.manageKilnForm.get('name')?.value || '').trim();
    if (name.length < 4) return false;
    const isKmt = this.manageKilnForm.get('is_kmt')?.value;
    if (isKmt) {
      const controllerSerial = (this.addFlowSerial || '').trim() || (this.manageKilnForm.get('serial_number')?.value || '').trim();
      if (!controllerSerial) return false;
    }
    return true;
  }

  submitNewKiln() {
    if (!this.manageKilnForm || !this.manageKilnForm.valid) {
      const name = this.manageKilnForm.get('name')?.value;
      if (!name || (name && name.length < 4)) {
        this.addFlowError = this.translate.instant('MANAGE_NETWORK.NAME_MIN_4_CHARS');
      } else {
        this.addFlowError = this.translate.instant('MANAGE_NETWORK.COMPLETE_REQUIRED_FIELDS');
      }
      return;
    }
    const formValue = { ...this.manageKilnForm.value, type: 'kiln' };
    const name = formValue.name;
    const serial = this.addFlowSerial && this.addFlowSerial.trim() ? this.addFlowSerial.trim() : null;
    const currentAccountId = this.accountInformation?.id;
    this.submittingNewKiln = true;
    this.addFlowError = '';

    const doUpsert = (externalId?: string, omitName = false) => {
      const id = externalId || serial;
      const upsertPayload: any = { ...formValue };
      if(upsertPayload.is_kmt) { delete upsertPayload.network_device; }
      delete upsertPayload.external_id;
      if (omitName) delete upsertPayload.name;

      return this.accountService.upsertKilnForAccount(id, upsertPayload);
    };

    const onSuccess = () => {
      this.userService.setCurrentAccount(this.accountInformation?.general?.external_id);
      this.upsertSuccess(this.translate.instant('MANAGE_NETWORK.KILN_SUCCESSFULLY_ADDED'));
      this.userService.updateLoginData();
    };

    if (serial) {
      const claimPayload = { ...formValue };
      delete claimPayload.external_id;
      delete claimPayload.type;
      delete claimPayload.network_device;
      this.kilnService.addKiln(serial, name, null, claimPayload, currentAccountId)
        .then(() => onSuccess())
        .catch((err) => {
          this.submittingNewKiln = false;
          this.addFlowError = this.translate.instant('ADD_KILN.COULD_NOT_ADD_KILN');
        });
    } else {
      doUpsert()
        .then(() => onSuccess())
        .catch((err) => {
          this.submittingNewKiln = false;
          this.upsertFailed(err);
        });
    }
  }

  async removeKiln() {
    const alert = await this.alertController.create({
      header: this.translate.instant('MANAGE_NETWORK.DELETE_CONFIRMATION'),
      message: this.translate.instant('MANAGE_NETWORK.DELETE_KILN_CONFIRM_MESSAGE', { kilnName: this.selectedKiln.name }),
      buttons: [
        {
          text: this.translate.instant('MANAGE_NETWORK.YES_DELETE'),
          role: 'delete',
          cssClass: 'primary',
          handler: (data) => {
            this.accountService.unlinkNetworkDevice(this.accountInformation.id, {id: this.selectedKiln.id})
                .then((response) => {
                  this.userService.setCurrentAccount(this.accountInformation.general.external_id);
                  this.showMessage(this.translate.instant('MANAGE_NETWORK.DEVICE_REMOVED'));
                  this.getAccountDevices();
                  this.onDismiss();
                })
                .catch((response) => { this.upsertFailed(this.translate.instant('MANAGE_NETWORK.COULD_NOT_REMOVE_KILN')); })
          }
        }, {
          text: this.translate.instant('MANAGE_NETWORK.NO_CANCEL'),
          cssClass: 'secondary',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  addNetworkDevice() {}

  setUpCanAdminister(data) {
    if(this.accountInformation && this.accountInformation.users && this.loginData) {
      let loggedInUserPermissions = this.accountInformation.users.find((u) => {
        return u.email ? (u.email === this.loginData.email) : (u.username === this.loginData.username)
      })

      this.canAdminister = loggedInUserPermissions ? (loggedInUserPermissions.type === 'admin') : false;
    }
  }

  getNdName(nd) {
    return nd.name ? nd.name : this.translate.instant('MANAGE_NETWORK.NAME_UNAVAILABLE_MAC', { mac: nd.mac_address || '' });
  }

  getPickerButtons() {
    return [
      {
        text: this.translate.instant('MANAGE_NETWORK.PICKER_CANCEL'),
        role: 'cancel',
      },
      {
        text: this.translate.instant('MANAGE_NETWORK.PICKER_CONFIRM'),
        handler: (selection) => {
          if(this.selected_tab.includes('kilns')) {
            this.selectKiln(selection.devices.value);
          } else {
            this.selecteNetworkDevice(selection.devices.value);
          }
        },
      },
    ];
  }

  async showPicker() {
    let options = []
    if(this.selected_tab.includes('kilns')) {
      options = this.kilns.map((k) => { return {text: k.name, value: k}})
    } else {
      options = this.network_devices.map((nd) => { return {text: nd.name, value: nd}})
    }        
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'devices',
        options: options
      }],
      buttons: this.getPickerButtons()
    });

    await picker.present();
  }

  goBack() {
    this.router.navigate(["/landing"]);
  }
}
